import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { selectAdminToken } from "../../admin/sessionSlice";
import { fetchThemeData } from "../../admin/themeSlice";
import { setBackButtonUrl } from "../uiSlice";
import { GAME_UPLOAD_PATH } from "../../utils/playerNavigation";
import { apiUrl } from "../../utils/apiUrl";
import { s3ThemeAssetUrl } from "../../utils/themeAssets";
import { isVotingOpen } from "../../utils/talentTheme";
import hartImg from "../../img/hart.png";
import laughImg from "../../img/laugh.png";
import clapImg from "../../img/clap.png";
import "../../components/stage/stageShell.css";
import "./gallery.css";

const PAGE_SIZE = 16;

const REACTIONS = [
  { key: "heart", emoji: "❤️", label: "Love", confetti: hartImg },
  { key: "smile", emoji: "😄", label: "Smile", confetti: laughImg },
  { key: "clap", emoji: "👏", label: "Clap", confetti: clapImg },
];

const REACTION_IMG = {
  heart: hartImg,
  smile: laughImg,
  clap: clapImg,
};

const emptyReactions = () => ({ heart: 0, smile: 0, clap: 0 });
const emptyMine = () => ({ heart: false, smile: false, clap: false });

const isVideoFile = (file) => /\.(mp4|mov)$/i.test(file || "");

function burstConfetti(reaction) {
  const src = REACTION_IMG[reaction];
  if (!src) return;
  // Respect users who ask for less motion.
  if (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
    return;
  }

  let layer = document.querySelector(".gal-confetti-layer");
  if (!layer) {
    layer = document.createElement("div");
    layer.className = "gal-confetti-layer";
    document.body.appendChild(layer);
  }

  const count = 24;
  for (let i = 0; i < count; i++) {
    const bit = document.createElement("img");
    bit.src = src;
    bit.alt = "";
    bit.className = "gal-confetti-bit";
    bit.setAttribute("aria-hidden", "true");
    const dx = (Math.random() - 0.5) * 220;
    const dy = -100 - Math.random() * 220;
    const rot = (Math.random() - 0.5) * 140;
    const delay = Math.random() * 0.35;
    const size = 22 + Math.random() * 18;
    bit.style.setProperty("--dx", `${dx}px`);
    bit.style.setProperty("--dy", `${dy}px`);
    bit.style.setProperty("--rot", `${rot}deg`);
    bit.style.width = `${size}px`;
    bit.style.height = `${size}px`;
    bit.style.left = `${Math.random() * 100}%`;
    bit.style.top = `${Math.random() * 100}%`;
    bit.style.animationDelay = `${delay}s`;
    layer.appendChild(bit);
    bit.addEventListener("animationend", () => {
      bit.remove();
      if (layer && !layer.childElementCount) layer.remove();
    });
  }
}

export default function TalentGallery() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: themeData, status, error: themeError } = useSelector((s) => s.theme);
  const user = useSelector((s) => s.auth.user);
  const adminToken = useSelector(selectAdminToken);

  const categories = useMemo(() => {
    const names = Array.isArray(themeData?.default_category_name)
      ? themeData.default_category_name
      : [];
    return ["All", ...names.filter((n) => String(n).toUpperCase() !== "ALL")];
  }, [themeData]);

  const showLeaderboard =
    String(themeData?.leaderboard_display ?? "false").trim().toLowerCase() === "true";
  const mosaicEnabled =
    String(themeData?.mosaic_enabled ?? "false").trim().toLowerCase() === "true";
  const categoryVotingOpen = isVotingOpen(themeData);

  const [uploads, setUploads] = useState([]);
  const [category, setCategory] = useState("All");
  const [sort, setSort] = useState("most_liked");
  const [q, setQ] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [likingKey, setLikingKey] = useState(null);
  const [votingId, setVotingId] = useState(null);
  const [votingOpen, setVotingOpen] = useState(true);
  const [leaderboardDisplay, setLeaderboardDisplay] = useState(showLeaderboard);
  const [votesPerCategory, setVotesPerCategory] = useState(1);
  const [myCategoryVoteCounts, setMyCategoryVoteCounts] = useState({});
  const [preview, setPreview] = useState(null);
  const [hasUpload, setHasUpload] = useState(false);
  const [canUploadMore, setCanUploadMore] = useState(true);
  const [galleryDisplayOpen, setGalleryDisplayOpen] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  const galleryClosed = !galleryDisplayOpen;

  useEffect(() => {
    setLeaderboardDisplay(showLeaderboard);
  }, [showLeaderboard]);

  useEffect(() => {
    if (themeData && "gallery_display" in themeData) {
      setGalleryDisplayOpen(
        !["false", "0", "closed"].includes(
          String(themeData.gallery_display ?? "true").trim().toLowerCase()
        )
      );
    }
  }, [themeData?.gallery_display]);

  useEffect(() => {
    dispatch(setBackButtonUrl(GAME_UPLOAD_PATH));
  }, [dispatch]);

  useEffect(() => {
    setPage(1);
  }, [category, q, sort]);

  useEffect(() => {
    if (!preview) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setPreview(null);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [preview]);

  // Cancels the previous request when filters change quickly, so an older (slower) response
  // can never overwrite a newer one.
  const galleryAbortRef = useRef(null);

  const loadGallery = useCallback(async () => {
    if (!adminToken) {
      setLoading(false);
      return;
    }
    galleryAbortRef.current?.abort();
    const controller = new AbortController();
    galleryAbortRef.current = controller;
    setLoading(true);
    try {
      const resp = await fetch(apiUrl("/talentGallery"), {
        signal: controller.signal,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          userId: user?.userId || "",
          category,
          q,
          sort,
        }),
      });
      const payload = await resp.json();
      if (controller.signal.aborted) return;
      if (resp.ok && payload?.success) {
        setUploads(Array.isArray(payload.uploads) ? payload.uploads : []);
        if (typeof payload.votingOpen === "boolean") setVotingOpen(payload.votingOpen);
        if (typeof payload.leaderboardDisplay === "boolean") {
          setLeaderboardDisplay(payload.leaderboardDisplay);
        }
        if (typeof payload.galleryDisplay === "boolean") {
          setGalleryDisplayOpen(payload.galleryDisplay);
        }
        const votedCategories = Array.isArray(payload.myVotedCategories)
          ? payload.myVotedCategories
          : [];
        if (payload.votesPerCategory != null) {
          setVotesPerCategory(Number(payload.votesPerCategory) || 1);
        }
        if (payload.myCategoryVoteCounts && typeof payload.myCategoryVoteCounts === "object") {
          setMyCategoryVoteCounts(payload.myCategoryVoteCounts);
        } else {
          const counts = {};
          votedCategories.forEach((c) => {
            counts[c] = (counts[c] || 0) + 1;
          });
          setMyCategoryVoteCounts(counts);
        }
      } else {
        setUploads([]);
      }
    } catch (e) {
      if (e?.name === "AbortError") return;
      console.error("talentGallery", e);
      setUploads([]);
    } finally {
      if (galleryAbortRef.current === controller) setLoading(false);
    }
  }, [adminToken, category, q, sort, user?.userId]);

  useEffect(() => {
    loadGallery();
    return () => galleryAbortRef.current?.abort();
  }, [loadGallery]);

  useEffect(() => {
    if (!adminToken || !user?.userId) {
      setHasUpload(false);
      setCanUploadMore(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(apiUrl("/spotGameStatus"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({ userId: user.userId }),
        });
        const json = await resp.json().catch(() => ({}));
        if (!cancelled) {
          setHasUpload(Boolean(json?.hasUpload));
          // Prefer API flag; fall back to theme if older backend
          if (typeof json?.canUploadMore === "boolean") {
            setCanUploadMore(json.canUploadMore);
          } else {
            const multiple =
              String(themeData?.badges_share_multiple ?? "").trim().toLowerCase() ===
              "true";
            setCanUploadMore(!json?.hasUpload || multiple);
          }
        }
      } catch {
        if (!cancelled) {
          setHasUpload(false);
          setCanUploadMore(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [adminToken, user?.userId, themeData?.badges_share_multiple]);

  const totalPages = Math.max(1, Math.ceil(uploads.length / PAGE_SIZE));
  const pageItems = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return uploads.slice(start, start + PAGE_SIZE);
  }, [uploads, page]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pagerNums = useMemo(() => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    return [1, 2, 3, 4, "…", totalPages];
  }, [totalPages]);

  const handleReact = async (gameId, reaction) => {
    if (!user || !adminToken) return;
    // Only block a second click on the same emoji while that request is in flight.
    // Other emojis stay clickable so the user can freely switch (shuffle counts).
    const lock = `${gameId}-${reaction}`;
    if (likingKey === lock) return;

    setLikingKey(lock);
    try {
      const resp = await fetch(apiUrl("/yogaLike"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({ userId: user.userId, gameId, reaction }),
      });
      const payload = await resp.json();
      if (resp.ok && payload?.success) {
        setUploads((prev) =>
          prev.map((row) => {
            if (row.id !== gameId) return row;
            const nextCounts = payload.reactionCounts
              ? { ...emptyReactions(), ...payload.reactionCounts }
              : { ...emptyReactions(), ...(row.reactionCounts || {}) };
            const myReactions = {
              heart: payload.liked && payload.reaction === "heart",
              smile: payload.liked && payload.reaction === "smile",
              clap: payload.liked && payload.reaction === "clap",
            };
            return {
              ...row,
              reactionCounts: nextCounts,
              myReactions,
              likedByMe: !!payload.liked,
              likeCount: payload.likeCount ?? row.likeCount,
            };
          })
        );
        if (payload.liked) {
          burstConfetti(reaction);
        }
      } else if (payload?.reactionCounts) {
        setUploads((prev) =>
          prev.map((row) =>
            row.id === gameId
              ? { ...row, reactionCounts: { ...emptyReactions(), ...payload.reactionCounts } }
              : row
          )
        );
      }
    } catch (e) {
      console.error("yogaLike", e);
    } finally {
      setLikingKey((prev) => (prev === lock ? null : prev));
    }
  };

  const handleVote = async (item) => {
    if (!categoryVotingOpen || !votingOpen || !user || !adminToken || votingId) return;
    const cat = String(item.category || "").trim();
    if (!cat) {
      Swal.fire("No category", "This entry has no category to vote in.", "warning");
      return;
    }
    const currentCount = myCategoryVoteCounts[cat] || 0;
    if (!item.votedByMe && currentCount >= votesPerCategory) {
      Swal.fire(
        "Vote limit reached",
        `You've used all ${votesPerCategory} vote${votesPerCategory === 1 ? "" : "s"} in the "${cat}" category.`,
        "info"
      );
      return;
    }
    setVotingId(item.id);
    try {
      const resp = await fetch(apiUrl("/categoryVote"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({ userId: user.userId, gameId: item.id }),
      });
      const payload = await resp.json();
      if (!resp.ok || !payload?.success) {
        throw new Error(payload?.error || "Vote failed");
      }
      setUploads((prev) =>
        prev.map((u) => {
          if (u.id !== item.id) {
            if (payload.voted && votesPerCategory <= 1 && String(u.category) === cat) {
              return { ...u, votedByMe: false };
            }
            return u;
          }
          return {
            ...u,
            votedByMe: !!payload.voted,
            voteCount:
              payload.voteCount != null ? payload.voteCount : u.voteCount || 0,
          };
        })
      );
      setMyCategoryVoteCounts((prev) => {
        if (payload.votesUsedInCategory != null) {
          return { ...prev, [cat]: Number(payload.votesUsedInCategory) || 0 };
        }
        const next = { ...prev };
        const existing = next[cat] || 0;
        next[cat] = payload.voted ? existing + 1 : Math.max(0, existing - 1);
        return next;
      });
    } catch (e) {
      Swal.fire("Vote", e.message || "Could not vote.", "error");
    } finally {
      setVotingId(null);
    }
  };

  const handleDelete = async (item) => {
    if (!user?.userId || !adminToken || deletingId) return;
    const result = await Swal.fire({
      title: "Delete your post?",
      text: "This removes your upload from the gallery. This cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
    });
    if (!result.isConfirmed) return;

    setDeletingId(item.id);
    try {
      const resp = await fetch(apiUrl("/talentDelete"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({ userId: user.userId, gameId: item.id }),
      });
      const payload = await resp.json();
      if (!resp.ok || !payload?.success) {
        throw new Error(payload?.error || "Delete failed");
      }
      setUploads((prev) => {
        const next = prev.filter((u) => u.id !== item.id);
        const stillHasMine = next.some((u) => u.isMine);
        if (!stillHasMine) {
          setHasUpload(false);
          setCanUploadMore(true);
        }
        return next;
      });
      if (preview?.id === item.id) setPreview(null);
      Swal.fire("Deleted", "Your post has been removed.", "success");
    } catch (e) {
      Swal.fire("Delete", e.message || "Could not delete post.", "error");
    } finally {
      setDeletingId(null);
    }
  };

  const applySearch = () => setQ(searchInput.trim());

  if (status === "loading" || status === "idle") {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader" />
      </div>
    );
  }

  if (status === "failed" || !themeData) {
    return (
      <div className="stage-shell">
        <p style={{ textAlign: "center" }}>
          Could not load gallery.{themeError ? ` (${themeError})` : ""}
        </p>
        <button
          type="button"
          className="stage-cta"
          style={{ display: "block", margin: "16px auto" }}
          onClick={() => dispatch(fetchThemeData({ themeId: null }))}
        >
          Retry
        </button>
      </div>
    );
  }

  const canShowLeaderboard = leaderboardDisplay;
  const canVote = categoryVotingOpen && votingOpen;

  return (
    <div className="stage-shell gal-page">
      <div className="stage-inner">
        <div className="gal-header">
          <h1 className="gal-title">Talent gallery</h1>
          <div className="gal-filters">
            <div className="gal-search">
              <span className="gal-search-icon" aria-hidden>
                🔍
              </span>
              <input
                type="search"
                placeholder="Search talents..."
                value={searchInput}
                onChange={(e) => {
                  const next = e.target.value;
                  setSearchInput(next);
                  if (!next.trim()) setQ("");
                }}
                onSearch={(e) => {
                  const next = e.currentTarget.value;
                  setSearchInput(next);
                  setQ(next.trim());
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") applySearch();
                }}
                onBlur={applySearch}
              />
            </div>
            <div className="gal-cat-filter">
              <span className="gal-cat-label">Select category</span>
              <div className="gal-cat-pill">
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  aria-label="Select category"
                >
                  {categories.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="gal-sort-filter">
              <span className="gal-cat-label">Sort by</span>
              <div className="gal-cat-pill">
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  aria-label="Sort gallery"
                >
                  <option value="most_liked">Most liked</option>
                  <option value="newest">Newest</option>
                </select>
              </div>
            </div>
            {canShowLeaderboard ? (
              <button
                type="button"
                className="gal-leaderboard-btn"
                onClick={() => navigate("/leaderboard")}
              >
                Leaderboard
              </button>
            ) : null}
            {mosaicEnabled ? (
              <button
                type="button"
                className="gal-leaderboard-btn"
                onClick={() => navigate("/mosaic")}
              >
                See Mosaic
              </button>
            ) : null}
            {canUploadMore ? (
              <button
                type="button"
                className="gal-upload-btn"
                onClick={() => navigate("/game")}
              >
                {hasUpload ? "Upload another" : "Upload your talent"}
              </button>
            ) : null}
          </div>
        </div>

        {galleryClosed ? (
          <p className="gal-pending-msg">
            Thank you for your submission. Your content will be visible in gallery
            once our moderators review it! Sit back and enjoy!
          </p>
        ) : loading ? (
          <div className="quiz-loader-container">
            <div className="quiz-loader" />
          </div>
        ) : pageItems.length === 0 ? (
          <p className="gal-empty">No talent entries found.</p>
        ) : (
          <div className="gal-grid">
            {pageItems.map((item) => {
              const counts = { ...emptyReactions(), ...(item.reactionCounts || {}) };
              const mine = { ...emptyMine(), ...(item.myReactions || {}) };
              const catLimitReached =
                !!item.category &&
                !item.votedByMe &&
                (myCategoryVoteCounts[String(item.category)] || 0) >= votesPerCategory;
              return (
                <article key={item.galleryKey || item.id} className="gal-card">
                  {item.isMine ? (
                    <button
                      type="button"
                      className="gal-delete-icon"
                      disabled={deletingId === item.id}
                      onClick={() => handleDelete(item)}
                      aria-label="Delete my post"
                      title="Delete my post"
                    >
                      ×
                    </button>
                  ) : null}
                  {item.category ? (
                    <span className="gal-tag">{item.category}</span>
                  ) : null}
                  <button
                    type="button"
                    className="gal-media"
                    onClick={() => setPreview(item)}
                    aria-label={`View ${item.name || "talent"} entry`}
                  >
                    {isVideoFile(item.uploadFile) ? (
                      <video
                        src={s3ThemeAssetUrl(item.uploadFile)}
                        muted
                        playsInline
                        preload="metadata"
                      />
                    ) : (
                      <img
                        src={s3ThemeAssetUrl(item.uploadFile)}
                        alt={item.name || "Talent"}
                        referrerPolicy="no-referrer"
                      />
                    )}
                  </button>
                  <h3>{item.name || "Anonymous"}</h3>
                  {item.message ? <p className="gal-msg">{item.message}</p> : null}
                  <div className="gal-reactions">
                    {REACTIONS.map((r) => (
                      <button
                        key={r.key}
                        type="button"
                        className={`gal-react-pill${mine[r.key] ? " on" : ""}`}
                        disabled={likingKey === `${item.id}-${r.key}`}
                        title={r.label}
                        onClick={() => handleReact(item.id, r.key)}
                      >
                        <span aria-hidden>{r.emoji}</span>
                        <span>{counts[r.key] ?? 0}</span>
                      </button>
                    ))}
                  </div>
                  {canVote ? (
                    <button
                      type="button"
                      className={`gal-vote-btn${item.votedByMe ? " on" : ""}`}
                      disabled={votingId === item.id || catLimitReached}
                      onClick={() => handleVote(item)}
                    >
                      {votingId === item.id
                        ? "Saving…"
                        : item.votedByMe
                          ? `Voted · ${item.voteCount || 0}`
                          : catLimitReached
                            ? "Vote limit reached"
                            : `Vote · ${item.voteCount || 0}`}
                    </button>
                  ) : null}
                </article>
              );
            })}
          </div>
        )}

        {!galleryClosed && totalPages > 1 && (
          <div className="gal-pager">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              aria-label="Previous page"
            >
              ‹
            </button>
            {pagerNums.map((n, i) =>
              n === "…" ? (
                <span key={`e-${i}`} className="gal-pager-ellipsis">
                  …
                </span>
              ) : (
                <button
                  key={n}
                  type="button"
                  className={page === n ? "active" : ""}
                  onClick={() => setPage(n)}
                >
                  {n}
                </button>
              )
            )}
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              aria-label="Next page"
            >
              ›
            </button>
          </div>
        )}
      </div>

      {preview ? (
        <div
          className="gal-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Talent preview"
          onClick={() => setPreview(null)}
        >
          <div
            className="gal-lightbox-panel"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="gal-lightbox-close"
              onClick={() => setPreview(null)}
              aria-label="Close"
            >
              ×
            </button>
            <div className="gal-lightbox-media">
              {isVideoFile(preview.uploadFile) ? (
                <video
                  src={s3ThemeAssetUrl(preview.uploadFile)}
                  controls
                  playsInline
                  autoPlay
                />
              ) : (
                <img
                  src={s3ThemeAssetUrl(preview.uploadFile)}
                  alt={preview.name || "Talent"}
                  referrerPolicy="no-referrer"
                />
              )}
            </div>
            <div className="gal-lightbox-meta">
              {preview.category ? (
                <span className="gal-lightbox-tag">{preview.category}</span>
              ) : null}
              <h3>{preview.name || "Anonymous"}</h3>
              {preview.message ? (
                <p className="gal-lightbox-title">{preview.message}</p>
              ) : null}
              {preview.descriptions ? (
                <p className="gal-lightbox-desc">{preview.descriptions}</p>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
